// Storage paths for the files the browser uploads.
//
//     {scope}/{family}/{ownerId}/{version}.{extension}
//
//     scope    users/{companyId}                    workspace-level
//            | users/{companyId}/tables/{tableId}   table-level
//     family   files, unallocated_files, ...  — the kind of artifact
//     ownerId  the id of the Firestore document that owns it
//     version  a fresh random token per write
//
// Keep this in sync by hand with functions/common/blob_paths.py, which builds
// the same paths on the backend and which nothing here can import.
//
// Nothing in the path means anything. The display filename lives on the
// document (`name`), and so does everything else a reader needs — a page image's
// page number, a unit's sheet. Never parse one of these paths; read the document
// instead. The extension is the one exception, kept because format validation
// and content types read it.

export const FILES = 'files';
// A unit's process summaries. The browser writes one kind of object here: a new
// version of a spilled Classify record it has edited (services/cleaningGestures.js).
export const PROCESS_SUMMARIES = 'process_summaries';
export const UNALLOCATED_FILES = 'unallocated_files';
export const PRE_CONVERTED = 'pre_converted';

// The processing family of each accepted upload extension, as file_type_of in
// functions/common/blob_paths.py: a workbook is read by Segment, a document by
// RecordSets. A .docx keeps its bytes; the document chain renders it to PDF.
const FILE_TYPES = {
  xlsx: 'xlsx', xls: 'xlsx', xlsm: 'xlsx', pdf: 'pdf', docx: 'pdf', doc: 'pdf',
};

// A version name: 32 lowercase hex characters, like the backend's uuid4().hex.
// Opaque on purpose — a reader that could parse it would start depending on it —
// and the same shape everywhere, so a token is recognisable as one without being
// readable.
//
// It only has to be unique inside one owner's folder, which holds a handful of
// objects over its whole life. randomUUID needs a secure context, which every
// browser gives an https page and localhost; where it is missing (a plain-http
// dev server, jsdom) the time-and-random fallback the rest of the app already
// uses for local ids is more than enough.
const TOKEN_LENGTH = 32;

export function versionToken() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '');
  }
  let token = Date.now().toString(16);
  while (token.length < TOKEN_LENGTH) token += Math.random().toString(16).slice(2);
  return token.slice(0, TOKEN_LENGTH);
}

// The extension a filename carries, lowercased and without the dot. Empty when
// it has none — the backend rejects those on content validation.
export function extensionOf(fileName) {
  const name = fileName || '';
  const dot = name.lastIndexOf('.');
  return dot < 0 ? '' : name.slice(dot + 1).toLowerCase();
}

// 'xlsx' or 'pdf' for a file name; throws for any other extension, which the
// upload doors refuse before registration (ACCEPTED_UPLOAD_EXTENSIONS).
export function fileTypeOf(fileName) {
  const extension = extensionOf(fileName);
  if (!Object.hasOwn(FILE_TYPES, extension)) {
    throw new Error(`Unsupported file extension "${extension}"`);
  }
  return FILE_TYPES[extension];
}

// A path no object occupies, for the file about to be uploaded.
export function newVersion(scope, family, ownerId, fileName) {
  const extension = extensionOf(fileName);
  const leaf = extension ? `${versionToken()}.${extension}` : versionToken();
  return `${scope}/${family}/${ownerId}/${leaf}`;
}

export const companyScope = (companyId) => `users/${companyId}`;
export const tableScope = (companyId, tableId) => `users/${companyId}/tables/${tableId}`;

// The display name for a file's pre-conversion original.
//
// The document's `name` is the canonicalised one — an .xls upload is stored and
// shown as .xlsx — and the stored path is a version token, so neither alone
// names the original. This takes the document's name and gives it back the
// extension the original actually carries.
export function preConvertedName(documentName, preConvertedPrefix) {
  const extension = extensionOf(preConvertedPrefix);
  const base = (documentName || 'original').replace(/\.[^./]*$/, '');
  return extension ? `${base}.${extension}` : base;
}
